import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { GetServerSidePropsContext, NextPage } from 'next';
import { useState } from 'react';
import Button from '../../../components/button/button.component';
import Container from '../../../components/container/container.component';
import SectionTitle from '../../../components/section-title/section-title.component';
import { getMenuAndOptions } from '../../../lib';
import styles from '../../../styles/search-page.module.scss';
import PageTemplate from '../../../templates/page.template';
import { MappedOptions } from '../../../types/mapped-options-and-menus.interface';
import { Menu } from '../../../types/menu.interface';

interface Props {
  menu: Menu;
  options: MappedOptions;
}

const FIELDS: string[] = [
  'id',
  'inventoryNumber',
  'season',
  'sca',
  'siteId',
  'trenchNumber',
  'locusNumber',
  'pbNo',
  'objectNo',
  'objectId',
  'parallels',
  'pbId',
  'preservation',
  'quantity',
  'weight',
  'datingAbsolute',
  'material',
  'classification',
  'type',
];
const OPERATORS: string[] = ['AND', 'OR'];
const CONDITIONS: string[] = ['IS', 'IS NOT', 'CONTAINS', 'DOES NOT CONTAIN'];


const FindsSearchForm: NextPage<Props> = ({ menu, options }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([
    { field: '', condition: '', fieldValue: '', operator: '' },
    { field: '', condition: '', fieldValue: '', operator: '' },
  ]);

  const addRow = () => {
    setRows([...rows, { field: '', condition: '', fieldValue: '', operator: '' }]);
  };

  const removeRow = (idx: number) => {
    setRows(rows.filter((_, i) => i !== idx));
  };

  const handleChange = (idx: number, field: string, value: string) => {
    setRows(rows.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  };

  return (
    <PageTemplate
      options={options}
      menu={menu}
      description=""
      title="Search finds"
    >
      <Container
        css={{
          height: 'auto',
          minHeight: 'calc(100vh - 240px)',
          marginTop: '80px',
        }}
        column={true}
      >
        <SectionTitle title="Search finds" />

        {/* Search form */}
        <form
          className={`${styles.form} visibleSimple`}
          action="/finds/search/results"
        >
          <input
            className={styles.largeInput}
            type="text"
            placeholder="Search for.."
            name="q"
            required
          />
          <Button btn={true} type="submit" css={{ margin: 'auto' }}>
            Search
          </Button>
        </form>
        <div
          style={{
            width: '100%',
            marginBottom: '2rem',
          }}
        >
          <button
            type="button"
            style={{
              width: '100%',
              padding: '1rem',
              textAlign: 'left',
              background: 'transparent',
              border: 'none',
              borderBottom: '1px solid #000',
              cursor: 'pointer',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '2rem',
            }}
            onClick={() => setOpen((o) => !o)}
          >
            <h3 style={{ fontSize: '32px', fontWeight: 400 }}>
              Advance search
            </h3>
            <FontAwesomeIcon
              style={{ fontSize: '24px' }}
              icon={open ? faCaretUp : faCaretDown}
            />
          </button>
          {open && (
            <div style={{ padding: '1rem', background: '#fff' }}>
              <form onSubmit={e => {
                e.preventDefault();
                const filter = rows
                  .filter(row => row.field && row.condition && row.fieldValue)
                  .map((row, idx) => {
                    let conditionStr = '';
                    switch (row.condition) {
                      case 'IS':
                        conditionStr = `${row.field} = "${row.fieldValue}"`;
                        break;
                      case 'IS NOT':
                        conditionStr = `${row.field} != "${row.fieldValue}"`;
                        break;
                      case 'CONTAINS':
                        conditionStr = `${row.field} LIKE "%${row.fieldValue}%"`;
                        break;
                      case 'DOES NOT CONTAIN':
                        conditionStr = `${row.field} NOT LIKE "%${row.fieldValue}%"`;
                        break;
                      default:
                        break;
                    }
                    if (idx > 0 && row.operator) {
                      conditionStr = `${row.operator} ${conditionStr}`;
                    }
                    return conditionStr;
                  })
                  .join(' ');
                console.log('Meilisearch filter:', filter);
                const params = new URLSearchParams();
                params.set('q', '*');
                params.set('filters', filter);
                window.location.href = `/finds/search/results?${params.toString()}`;
              }}>
                {rows.map((row, idx) => (
                  <div key={idx} style={{ display: 'flex', marginBottom: '1rem', alignItems: 'center' }}>
                    {/* First row: three fields, two selects and text input */}
                    {idx === 0 ? (
                      <>
                        <div style={{ width: '15%' }}></div>
                        <select className={styles.advanceInput} value={row.field} onChange={e => handleChange(idx, 'field', e.target.value)} style={{ width: '30%' }}>
                          <option value="">Select Field</option>
                          {FIELDS.map((field) => (
                            <option key={field} value={field}>
                              {field
                                .replace(/([A-Z])/g, ' $1')
                                .replace(/^./, str => str.toUpperCase())
                              }
                            </option>
                          ))}
                        </select>
                        <select className={styles.advanceInput} value={row.condition} onChange={e => handleChange(idx, 'condition', e.target.value)} style={{ width: '15%' }}>
                          <option value="">Select Condition</option>
                          {CONDITIONS.map((condition) => (
                            <option key={condition} value={condition}>{condition}</option>
                          ))}
                        </select>
                        <input type="text" value={row.fieldValue} onChange={e => handleChange(idx, 'fieldValue', e.target.value)} placeholder="Field Value" className={styles.advanceInput} style={{ width: '30%' }} />
                        <div style={{ width: '10%' }} className={styles.endPlaceholder}></div>
                      </>
                    ) : (
                      <>
                        {/* Second and subsequent rows: three selects and text inputs, plus controls */}
                        <select className={styles.advanceInput} value={row.operator} onChange={e => handleChange(idx, 'operator', e.target.value)} style={{ width: '15%' }}>
                          <option value="">Select Operator</option>
                          {OPERATORS.map((operator) => (
                            <option key={operator} value={operator}>{operator}</option>
                          ))}
                        </select>
                        <select className={styles.advanceInput} value={row.field} onChange={e => handleChange(idx, 'field', e.target.value)} style={{ width: '30%' }}>
                          <option value="">Select Field</option>
                          {FIELDS.map((field) => (
                            <option key={field} value={field}>{field}</option>
                          ))}
                        </select>
                        <select className={styles.advanceInput} value={row.condition} onChange={e => handleChange(idx, 'condition', e.target.value)} style={{ width: '15%' }}>
                          <option value="">Select Condition</option>
                          {CONDITIONS.map((condition) => (
                            <option key={condition} value={condition}>{condition}</option>
                          ))}
                        </select>
                        <input type="text" value={row.fieldValue} onChange={e => handleChange(idx, 'fieldValue', e.target.value)} placeholder="Field Value" className={styles.advanceInput} style={{ width: '30%' }} />
                        <button type="button" onClick={addRow} style={{ width: '5%' }} className={styles.advanceButton}>+</button>
                        {idx > 1 ? (
                          <button type="button" onClick={() => removeRow(idx)} style={{ width: '5%' }} className={styles.advanceButton}>-</button>
                        ) : <div style={{ width: '5%' }} className={styles.endPlaceholder}></div>}
                      </>
                    )}
                  </div>
                ))}
                <Button btn={true} type="submit" css={{ margin: 'auto' }}>
                  Search
                </Button>
              </form>
            </div>
          )}
        </div>
      </Container>
    </PageTemplate>
  );
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const searchPage = await getMenuAndOptions();

  ctx.res.setHeader('Cache-Control', 'public, max-age=300, s-maxage=300');

  return {
    props: searchPage,
  };
}

export default FindsSearchForm;
